import React, { useState } from 'react';
import { GoogleMap, Marker, withGoogleMap } from 'react-google-maps';
import { Autocomplete, Box, IconButton, TextField, Stack } from '@mui/material';
import mapSyle from './MapStyle';
import { CompleteLocation } from '../@types/model/device';
import { mapsConfig } from '../config';
import SvgIconStyle from './SvgIconStyle';

interface CoordinatesPikerProps {
  onRemove: () => void;
  onClick: (marker: CompleteLocation) => void;
  marker: CompleteLocation | null;
  height: string | null;
  editionEnabled: boolean;
  className?: string;
  showSearchBox: boolean;
}

type LatLonPair = {
  lat: number;
  lon: number;
};

const Map = withGoogleMap((props: any) => (
  <GoogleMap
    options={{
      streetViewControl: false,
      mapTypeControl: false,
      fullscreenControl: false,
      styles: mapSyle
    }}
    zoom={4}
    center={
      props.marker
        ? { lat: props.marker.lat, lng: props.marker.lon }
        : { lat: 40.41671930808935, lng: -3.7034802872031567 }
    }
    onClick={(event) => {
      props.onClick({ lat: event.latLng.lat(), lon: event.latLng.lng() } as LatLonPair);
    }}
  >
    {props.marker ? <Marker position={{ lat: props.marker.lat, lng: props.marker.lon }} /> : null}
  </GoogleMap>
));

const geocodeAddress = (address: string, okCallback: (res: CompleteLocation[]) => void) => {
  fetch(
    `https://maps.googleapis.com/maps/api/geocode/json?key=${mapsConfig.apiKey}&address=` + address,
    {}
  )
    .then((data) => {
      return data.json();
    })
    .then((content) => {
      okCallback(
        content.results.map((r: any) => {
          return {
            identifier: r.place_id,
            formatedAddress: r.formatted_address,
            postalCode: r.address_components.find((item: any) => {
              return item.types.includes('postal_code');
            })?.long_name,
            locality: r.address_components.find((item: any) => {
              return item.types.includes('locality');
            })?.long_name,
            administrative_area_level_2: r.address_components.find((item: any) => {
              return item.types.includes('administrative_area_level_2');
            })?.long_name,
            administrative_area_level_1: r.address_components.find((item: any) => {
              return item.types.includes('administrative_area_level_1');
            })?.long_name,
            country: r.address_components.find((item: any) => {
              return item.types.includes('country');
            })?.long_name,
            lat: r.geometry.location.lat,
            lon: r.geometry.location.lng
          } as CompleteLocation;
        })
      );
    })
    .catch((error) => {
      console.log(error);
    });
};

export const CoordinatesPicker: React.FC<CoordinatesPikerProps> = (
  props: CoordinatesPikerProps
) => {
  const [newMarker, setNewMarker] = useState<LatLonPair | undefined>(
    props.marker
      ? ({
          lat: props.marker?.lat,
          lon: props.marker?.lon
        } as LatLonPair)
      : undefined
  );

  const [searchResult, setSearchResult] = useState<CompleteLocation[]>([]);

  const searchLocations = (searchAddress: string) => {
    if (searchAddress.length > 5) {
      geocodeAddress(searchAddress, (locations) => {
        setSearchResult(
          locations.map((l: CompleteLocation) => {
            return { key: l.identifier, ...l };
          })
        );
      });
    }
  };

  return (
    <div className="map">
      <Stack direction={'row'}>
        <Autocomplete
          fullWidth
          disablePortal
          autoHighlight
          sx={{ marginRight: '10px' }}
          disabled={!props.editionEnabled}
          filterOptions={(x) => x}
          onChange={(event, values) => {
            if (values && values.lat && values.lon) {
              setNewMarker({ lat: values.lat, lon: values.lon });
              props.onClick(values);
            }
          }}
          value={props.marker ? props.marker : null}
          options={searchResult}
          isOptionEqualToValue={(option, value) => {
            return option.identifier === value.identifier;
          }}
          getOptionLabel={(option) => (option.formatedAddress ? option.formatedAddress : '')}
          renderOption={(props, option) => (
            <li {...props} key={option.identifier}>
              {option.formatedAddress}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              value={props.marker ? props.marker : ''}
              label="Escriba la dirección del dispositivo y seleccione de la lista"
              onChange={(event) => {
                searchLocations(event.target.value);
              }}
              inputProps={{
                ...params.inputProps,
                autoComplete: 'new-password'
              }}
            />
          )}
        />
        <IconButton
          onClick={() => {
            setNewMarker(undefined);
            props.onRemove();
          }}
        >
          <SvgIconStyle
            src={'/static/icons/giraf/remove.svg'}
            sx={{
              width: '40px',
              height: '30px',
              display: 'block',
              color: 'red'
            }}
          />
        </IconButton>
      </Stack>
      <Box mt={'15px'}>
        <Map
          // tslint:disable-next-line
          isMarkerShown
          googleMapURL="https://maps.googleapis.com/maps/api/js?v=3.exp&libraries=geometry,drawing,places"
          loadingElement={<div style={{ height: `100%` }} />}
          containerElement={<div style={{ height: props.height ? props.height : `800px` }} />}
          mapElement={<div style={{ height: `100%` }} />}
          marker={newMarker}
          onClick={(marker: LatLonPair) => {
            if (props.marker) {
              props.onClick({
                identifier: props.marker!.identifier,
                formatedAddress: props.marker!.formatedAddress,
                postalCode: props.marker!.postalCode,
                locality: props.marker!.locality,
                administrative_area_level_2: props.marker!.administrative_area_level_2,
                administrative_area_level_1: props.marker!.administrative_area_level_1,
                country: props.marker!.country,
                lat: marker.lat,
                lon: marker.lon
              } as CompleteLocation);
              setNewMarker({ lat: marker.lat, lon: marker.lon });
            }
          }}
        />
      </Box>
    </div>
  );
};
